<template>
	<LayoutBreadcrumbs class="main-wrapper" :with-schema="false" />

	<div class="main-wrapper">
		<Category
			v-if="currentCategory && initialData"
			:current-category="currentCategory"
			:initial-data="initialData"
			:page-size="pageSize"
			:picture-aspect-ratio="pictureAspectRatio"
			:initial-current-sorting-order="initialCurrentSortingOrder"
			:products-population-config="productsPopulationConfig"
			:story-data="storyblokDataRef?.content"
			:advertisement="advertisement"
			:recommendations="recommendations"
			:initial-page-no="initialPageNo"
		/>
		<div v-else class="flex flex-col gap-4 mb-10">
			<div class="border-b pb-6 h-[155px]">
				<h1 class="text-3xl mb-4 animate-pulse w-[100px] h-10"></h1>
				<span class="max-w-screen-sm block animate-pulse w-[50px] h-10"></span>
			</div>
			<div class="flex-col lg:flex-row justify-between w-full flex">
				<div
					class="lg:w-[280px] relative h-[400px] gap-2 hidden lg:flex flex-col"
				>
					<div
						v-for="i in 6"
						:key="i"
						class="h-[50px] bg-gray-light border-gray-lighter border animate-pulse w-full"
					/>
				</div>
				<div class="flex flex-wrap relative flex-1 lg:ml-8">
					<div class="grid grid-cols-2 lg:grid-cols-3 gap-8 w-full">
						<div
							v-for="i in 6"
							:key="i"
							class="h-[300px] lg:h-[400px] bg-gray-light border-gray-lighter border animate-pulse w-full"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import type { Category as CategoryType } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	BlokSEOApp,
	AdvertisementBlok,
	BlokProductRecommendations
} from "~/types/storyblok/storyblok";
import type { InitialData } from "~/types/components/category";
import { storeToRefs } from "pinia";
import { FilterType } from "~/types/storyblok/productsPopulation";
import { computed, ref } from "vue";
import Category from "~/components/Category/Category.vue";
import { activeStatusID } from "~/utils/constants";
import { normalizeProductSort } from "~/utils/product/sort";
import { omitHiddenFilterSections } from "~/utils/categories/filterSections";

const props = defineProps<{
	localizedSeo?: Record<string, { name?: string; url?: string }>;
}>();
// [...slug].vue also passes `slug` / `unique-product-name`, which this page
// doesn't use; the root is a fragment (breadcrumbs + content), so they can't
// fall through and Vue warns. Drop them instead.
defineOptions({ inheritAttrs: false });

const { fetchStory } = useStoryblokFetcher();
const { locale, localeProperties } = useI18n();
const storyblokLanguage = useStoryblokLanguage();
const localePath = useLocalePath();
const route = useRoute();
// User preference (cookie) wins over the Storyblok default. Created in
// setup (before any await) so the Nuxt instance is available on SSR.
// Captured at setup: inside `useAsyncData`'s handler the Nuxt instance is gone
// after the first await, so these would throw during SSR.
const { showPageNotFound } = usePageNotFound();
const categoryStore = useCategoryStore();
const pageSizeCookie = useCookie<string | null>("productsPerPage", {
	maxAge: 60 * 60 * 24 * 365,
	sameSite: "lax"
});
const router = useRouter();

const config = useRuntimeConfig();
const ImagesCdnUrl = config.public.norce.cdn + "/";

const { getCategoryDetailsById, iterateOverCategoryDetails } = useCategory();
const pageStore = usePageStore();
const { setCategoryBreadcrumb, setPageType, setPageTitle } = pageStore;
const { categoryBreadcrumb } = storeToRefs(pageStore);
const { listProductByFilters, getCurrentCategory, mapProductFilters } =
	useNorcePwa();
const { fetchCategoryTree } = useFetchCategoryTree();
const { fetchAllParametrics } = useAllParametrics();
const norceStore = useNorceStore();
const { storeContext } = storeToRefs(norceStore);

const initialData = ref<InitialData>();
const currentCategory = ref<CategoryType>();
const pageSize = ref<string>("0");
const initialPageNo = ref<number>(1);
const seo = ref<BlokSEOApp>();
const pictureAspectRatio = ref<string>("4/3");
const initialCurrentSortingOrder = ref<string>(
	normalizeProductSort(route.query.sort)
);
const storyblokPopulationConfig = ref(null);
const productsPopulationConfig = ref();
const storyblokDataRef = ref();
const layoutHiddenFilters = ref<unknown>();
// Live-sync the per-category `categories-content` story with the Storyblok
// Visual Editor (no-op outside the editor). Registered before the awaits
// below so the onMounted hook lands while the instance is still active;
// `storyblokDataRef` is populated later by the awaited fetch.
useStoryblokBridgeStory(storyblokDataRef, { resolveLinks: "url" });
const advertisement = ref<AdvertisementBlok | null>(null);
const recommendations = ref<BlokProductRecommendations | null>(null);

const initialFetch = async () => {
	if (!locale.value) {
		return;
	}

	setPageType("category");

	const [categoryTree, allParametrics] = await Promise.all([
		fetchCategoryTree(locale.value),
		fetchAllParametrics()
	]);

	const fetchedCategory = await getCurrentCategory(
		{
			categoriesSlug: route.params.slug as string[]
		},
		{ categoryTree, currentCategoryId: categoryStore.getCategoryId! }
	);

	if (!fetchedCategory) {
		setPageType("");

		return showPageNotFound();
	}

	/*
		The canonical path is the whole chain, not the leaf.

		`urls` stores a category as `parent/child`, composed up the tree, so
		pushing a single kebab-cased name sent the visitor to a path the index has
		never contained — a guaranteed 404 for anything nested. The chain is built
		from the tree already fetched above, the same way the feed builds it.

		An empty chain means the tree could not place this category, in which case
		nothing is pushed: a wrong redirect is worse than none.
	*/
	const canonicalPath = iterateOverCategoryDetails(
		getCategoryDetailsById(
			categoryTree,
			fetchedCategory.CategoryId!,
			"CategoryId"
		)
	);
	const requestedPath = ((route.params.slug as string[]) ?? []).join("/");

	if (canonicalPath && canonicalPath !== requestedPath) {
		setPageType("");
		router.push(localePath(`/${canonicalPath}`));
	}
	currentCategory.value = fetchedCategory;
	const storyblokSeedConfig = getFlagSeedValue(
		storyblokPopulationConfig.value,
		fetchedCategory.CategoryId
	);
	const seedConfig = storyblokSeedConfig
		? { [storyblokSeedConfig.entity]: storyblokSeedConfig.id }
		: { categorySeed: fetchedCategory.CategoryId.toString() };
	productsPopulationConfig.value = seedConfig;

	const requestedPage = Math.max(1, parseInt(route.query.p as string, 10) || 1);
	initialPageNo.value = requestedPage;

	const [filters, products] = await Promise.all([
		mapProductFilters(
			{
				...seedConfig,
				// Same status as the listing, or facet counts include inactive products.
				statusSeed: String(activeStatusID),
				cultureCode: localeProperties.value.iso
			},
			{
				allParametrics
			}
		),
		listProductByFilters({
			statusSeed: String(activeStatusID),
			...seedConfig,
			pageNo: String(requestedPage),
			pageSize: pageSize.value,
			sort: initialCurrentSortingOrder.value,
			asVariants: true,
			processColorVariants: true,
			filterByAllowedColors: true,
			...storeContext.value,
			cultureCode: localeProperties.value.iso,
			...(route.query.filter ? { filter: route.query.filter as string } : {})
		})
	]);
	setCategoryBreadcrumb(
		getCategoryDetailsById(
			categoryTree,
			fetchedCategory.CategoryId,
			"CategoryId"
		)
	);
	setPageTitle(currentCategory.value.Name);
	setPageType("category");

	initialData.value = { products, ...filters, allParametrics };
	categoryStore.setCategoryId(currentCategory.value.CategoryId);
};

const getFlagSeedValue = (data, categoryId) => {
	const fields = Object.values(FilterType);

	for (const item of data) {
		for (const field of fields) {
			if (item[field]?.startsWith(`${categoryId}-`)) {
				return {
					id: item[field].split("-")[1],
					entity: field
				};
			}
		}
	}

	return null;
};
const getInitialDataFromStoryBlok = async () => {
	const { data } = await fetchStory("layouts/category-page", {
		version: config.public.storyblokVersion,
		resolve_links: "url",
		language: storyblokLanguage.value
	});

	layoutHiddenFilters.value = data.story.content.hidden_filters;
	const storyblokPageSize = data.story.content.products_page_count
		? data.story.content.products_page_count
		: "12";
	// useCookie JSON-decodes "12" to a number — keep pageSize a string.
	pageSize.value = pageSizeCookie.value
		? String(pageSizeCookie.value)
		: storyblokPageSize;
	pictureAspectRatio.value = data.story.content.images_aspect_ratio;
	seo.value = data.story.content.seo;
	storyblokPopulationConfig.value = data.story.content.population_config;
	advertisement.value = data.story.content.advertisement?.[0];
	recommendations.value = data.story.content.recommendations?.[0];
};

const loadCategoryStory = async () => {
	if (!currentCategory.value?.CategoryId) return;

	try {
		const { data } = await fetchStory(
			`${config.public.categoryContentKey}/${currentCategory.value.CategoryId}`,
			{
				version: config.public.storyblokVersion,
				language: storyblokLanguage.value,
				resolve_links: "url"
			}
		);
		storyblokDataRef.value = data?.story;
	} catch {
		storyblokDataRef.value = null;
	}
};

/**
 * The three phases above used to run as bare `await`s in setup, so hydration
 * repeated every one of them in the browser: layout story, category tree, all
 * parametrics, the category story, the filters and the product list (`pageSize`
 * up to 100). Running them inside `useAsyncData` means the server result travels
 * in the Nuxt payload and the client reuses it.
 *
 * The phases keep assigning the refs exactly as before — nothing inside them
 * changed. What's new is the snapshot: when the payload is reused the handler
 * never runs on the client, so the refs would stay empty. `captureSnapshot`
 * reads them after the fetch and `applySnapshot` writes them back on the client.
 */
const captureSnapshot = () => ({
	currentCategory: currentCategory.value,
	initialData:
		initialData.value &&
		omitHiddenFilterSections(
			initialData.value,
			layoutHiddenFilters.value,
			storyblokDataRef.value?.content?.hidden_filters
		),
	pageSize: pageSize.value,
	initialPageNo: initialPageNo.value,
	seo: seo.value,
	pictureAspectRatio: pictureAspectRatio.value,
	storyblokPopulationConfig: storyblokPopulationConfig.value,
	productsPopulationConfig: productsPopulationConfig.value,
	storyblokDataRef: storyblokDataRef.value,
	advertisement: advertisement.value,
	recommendations: recommendations.value
});

const applySnapshot = (snapshot: ReturnType<typeof captureSnapshot>) => {
	currentCategory.value = snapshot.currentCategory;
	initialData.value = snapshot.initialData;
	pageSize.value = snapshot.pageSize;
	initialPageNo.value = snapshot.initialPageNo;
	seo.value = snapshot.seo;
	pictureAspectRatio.value = snapshot.pictureAspectRatio;
	storyblokPopulationConfig.value = snapshot.storyblokPopulationConfig;
	productsPopulationConfig.value = snapshot.productsPopulationConfig;
	storyblokDataRef.value = snapshot.storyblokDataRef;
	advertisement.value = snapshot.advertisement;
	recommendations.value = snapshot.recommendations;
};

// The sales area belongs in the key, not only in the watch: the shopper's area
// lives in the browser, so the server renders with the application's default
// and the browser may hold the country chosen at checkout. `useAsyncData` reuses
// the server's payload whenever the key matches, so without this the listing
// keeps the prices rendered before a country was known.

const { data: categorySnapshot, error: categoryError } = await useAsyncData(
	`category:${route.path}:${norceStore.priceContextKey}`,
	async () => {
		await getInitialDataFromStoryBlok();
		await initialFetch();
		await loadCategoryStory();

		return captureSnapshot();
	},
	// Locale only. Page size is handled below, so it can't double-fetch here.
	{ watch: [locale] }
);

// A failed fetch is logged, not turned into an error page: `useAsyncData`
// captures the throw into `error`, and without this the page sits on the
// skeleton with nothing in the console — which is exactly how an SSR-only
// failure went unnoticed here while the other templates reported it.
if (categoryError.value) {
	console.error("[category] failed to load:", categoryError.value);
}

watch(
	categorySnapshot,
	(snapshot) => {
		if (snapshot) applySnapshot(snapshot);
	},
	{ immediate: true }
);

const storyblokSeo = computed(() => storyblokDataRef.value?.content?.SEO);

const seoImage = computed(() => {
	if (currentCategory.value?.ImageKey) {
		return ImagesCdnUrl + currentCategory.value?.ImageKey;
	} else if (storyblokSeo.value?.og_image) {
		return storyblokSeo.value.og_image;
	} else if (seo.value?.og_image) {
		return seo.value?.og_image;
	}

	return null;
});

const seoFields = computed(() => {
	const defaultTitle = `${currentCategory.value?.Name} - ${seo.value?.title}`;

	const {
		title: sbTitle,
		og_title: sbOgTitle,
		description: sbDescription,
		og_description: sbOgDescription
	} = storyblokSeo.value || {};

	return {
		title: sbTitle || defaultTitle,
		ogTitle: sbOgTitle || sbTitle || defaultTitle,
		description: sbDescription || seo.value?.description,
		ogDescription: sbOgDescription || seo.value?.og_description,
		ogImage: seoImage.value
	};
});

const requestURL = useRequestURL();
const domain = computed(() => requestURL.origin);

const productItemList = computed(() => {
	if (!initialData.value?.products?.Items?.length) {
		return null;
	}

	return defineItemList({
		numberOfItems: initialData.value.products.Items.length,
		itemListElement: initialData.value.products.Items.slice(0, 10).map(
			(product, index) => ({
				"@type": "ListItem",
				position: index + 1,
				name: product.Name || product.VariantName || "",
				url: product.UniqueName
					? `${domain.value}${localePath(`/${product.UniqueName}`)}`
					: undefined
			})
		)
	});
});

useSeoMeta({
	title: () => seoFields.value.title,
	ogTitle: () => seoFields.value.ogTitle,
	description: () => seoFields.value.description,
	ogDescription: () => seoFields.value.ogDescription
});

const canonicalUrlByIso = computed(() => {
	if (!props.localizedSeo) return undefined;
	const out: Record<string, string> = {};
	for (const [iso, entry] of Object.entries(props.localizedSeo)) {
		if (entry?.url) out[iso] = entry.url;
	}
	return Object.keys(out).length > 0 ? out : undefined;
});
useLocalizedCanonical(canonicalUrlByIso);

if (import.meta.server) {
	const appStore = useAppStore();
	if (seoImage.value) {
		defineOgImageComponent("OgCategory", {
			image: seoImage.value,
			logo: appStore.siteLogo?.filename || "",
			title: seoFields.value.ogTitle || "",
			description: seoFields.value.description || "",
			width: 1200,
			height: 630
		});
	}
}

const { buildBreadcrumbItems } = useCategoryBreadcrumb();

useSchemaOrg(
	computed(() => {
		const breadcrumbItems = buildBreadcrumbItems(categoryBreadcrumb.value);
		return [
			defineWebPage({
				"@type": "CollectionPage",
				name: currentCategory.value?.Name || "",
				description: seoFields.value.description || "",
				image: seoImage.value || undefined,
				...(productItemList.value ? { mainEntity: productItemList.value } : {})
			}),
			...(breadcrumbItems.length > 1
				? [defineBreadcrumb({ itemListElement: breadcrumbItems })]
				: [])
		];
	})
);

// Locale is covered by `useAsyncData`'s `watch` above. Page size is owned by
// the Category component, which writes the cookie and refetches the list
// itself — re-running the whole page fetch here would only duplicate that.
</script>
